import { create } from "zustand";
import { persist } from "zustand/middleware";

interface AuthState {
    /** CSRF token returned by login/refresh; sent as X-CSRF-Token on writes. */
    csrfToken: string | null;
    /** Session expiry (ISO string) from LoginPayload; drives the refresh scheduler. */
    expiresAt: string | null;
    /** True when a request hit 401 — triggers the re-login modal. */
    sessionExpired: boolean;
    /** Active workspace GID. Set ONLY by WorkspaceLayout. */
    wsId: string | null;
    /** Last workspace the user visited, for the root redirect. */
    lastWsId: string | null;

    setSession: (session: { csrfToken: string; expiresAt: string }) => void;
    clearSession: () => void;
    setSessionExpired: (expired: boolean) => void;
    setWsId: (wsId: string | null) => void;
}

export const useAuthStore = create<AuthState>()(
    persist(
        (set) => ({
            csrfToken: null,
            expiresAt: null,
            sessionExpired: false,
            wsId: null,
            lastWsId: null,

            setSession: ({ csrfToken, expiresAt }) =>
                set({ csrfToken, expiresAt, sessionExpired: false }),
            clearSession: () =>
                set({ csrfToken: null, expiresAt: null, sessionExpired: false, wsId: null }),
            setSessionExpired: (sessionExpired) => set({ sessionExpired }),
            setWsId: (wsId) =>
                set((state) => ({ wsId, lastWsId: wsId ?? state.lastWsId })),
        }),
        {
            name: "kh-auth",
            partialize: ({ csrfToken, expiresAt, lastWsId }) => ({
                csrfToken,
                expiresAt,
                lastWsId,
            }),
        },
    ),
);

// Non-React accessors for the axios layer and schedulers.
export const getCsrfToken = () => useAuthStore.getState().csrfToken;
export const getWsId = () => useAuthStore.getState().wsId;
